import '../../styles/_time-card.scss';

import { skipToken } from '@reduxjs/toolkit/dist/query';
import React, { useCallback,useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import { CleTimeRecord } from '../../datasource/generated';
import { useGetCleTimeRecordsQuery } from '../../datasource/queries/cle';
import { useAppSelector } from '../../hooks';
import Button from '../Button/Button';
import Loader from '../Loader/Loader';
import { ErrorContainer } from '../MainError/MainError';
import withPLIErrorBoundary from '../PliErrorBoundary/PliErrorBoundary';
import { TimeCard } from '../Timecard/TimeCard';

export type ITimeCardsProps = {
    onClose: (() => void) | null;
    onTimeCardsLoaded: (hasLoaded: boolean) => void;
};

const RECORD_TYPES = {
    LIVE: 'live',
    ON_DEMAND: 'onDemand',
} as const;

type RecordType = (typeof RECORD_TYPES)[keyof typeof RECORD_TYPES];

const RequestCreditTimeCardsBase = ({
    onClose,
    onTimeCardsLoaded,
}: ITimeCardsProps) => {
    const [expandedSections, setExpandedSections] = useState<Record<RecordType, boolean>>({
        live: false,
        onDemand: false,
    });

    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const encryptedRegistrationId = useAppSelector((state) => state.requestCredit.encryptedRegistrationId);

    const { showBoundary } = useErrorBoundary();

    const {
        data: timeRecords,
        isLoading: isLoadingTimeRecords,
        isSuccess: isGetTimeCardsSuccess,
        isError: isGetTimeCardsError,
    } = useGetCleTimeRecordsQuery(encryptedRegistrationId ?? skipToken);

    useEffect(() => {
        onTimeCardsLoaded(isGetTimeCardsSuccess);
    }, [isGetTimeCardsSuccess, onTimeCardsLoaded]);

    const { liveCleRecords, onDemandCleRecords } = useMemo(() => {
        if (!timeRecords) {
            return { liveCleRecords: undefined, onDemandCleRecords: undefined };
        }

        const liveRecords = timeRecords.filter(timeRecord => timeRecord.isLive);
        const onDemandRecords = timeRecords.filter(timeRecord => !timeRecord.isLive);

        if (!launchDetails.isWebcast && liveRecords.length) {
            const recordTemplates : CleTimeRecord[] = timeRecords
                .filter((value, index, self) => 
                    self.findIndex(v => v.prgSegment_SK === value.prgSegment_SK) === index)
                .map(value => {
                    return {
                        ...value,
                        percentComplete: 0,
                        isLive: false,
                    };
                });

            const ondemand = recordTemplates.map(recordTemplate => {
                return {
                    ...recordTemplate,
                    percentComplete: onDemandRecords.find(x => x.prgSegment_SK === recordTemplate.prgSegment_SK)?.percentComplete ?? 0,
                    isLive: false,
                };
            });

            const live = recordTemplates.map(recordTemplate => {
                return {
                    ...recordTemplate,
                    percentComplete: liveRecords.find(x => x.prgSegment_SK === recordTemplate.prgSegment_SK)?.percentComplete ?? 0,
                    isLive: true,
                };
            });

            return {
                liveCleRecords: live,
                onDemandCleRecords: ondemand,
            };
        }
        else {
            return {
                liveCleRecords: liveRecords,
                onDemandCleRecords: onDemandRecords,
            };
        }
    }, [timeRecords, launchDetails.isWebcast]);

    const hasLive = (liveCleRecords?.length ?? 0) > 0;
    const hasOnDemand = (onDemandCleRecords?.length ?? 0) > 0;
    const hasBoth = hasLive && hasOnDemand;
    const hasEither = hasLive || hasOnDemand;

    const toggleSection = useCallback((type: RecordType) => {
        setExpandedSections((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    }, []);

    const isExpanded = (type: RecordType) => expandedSections[type];

    const getVisibleRecords = (records: CleTimeRecord[] | undefined, type: RecordType) => {
        if (!records) return [];
        const isSectionExpanded = isExpanded(type);

        return records.filter(
            (record) => isSectionExpanded || (record.trackCle && (record.percentComplete ?? 0) > 0)
        );
    };

    const visibleLiveRecords = getVisibleRecords(liveCleRecords, RECORD_TYPES.LIVE);
    const visibleOnDemandRecords = getVisibleRecords(onDemandCleRecords, RECORD_TYPES.ON_DEMAND);

    const shouldShowToggle = (records: CleTimeRecord[] | undefined, type: RecordType): boolean => {
        if (!records || records.length === 0) return false;
        const expanded = isExpanded(type);
        if (expanded) return true;

        return records.some((r) => !(r.trackCle && (r.percentComplete ?? 0) > 0));
    };

    const showLiveToggle = shouldShowToggle(liveCleRecords, RECORD_TYPES.LIVE);
    const showOdToggle = shouldShowToggle(onDemandCleRecords, RECORD_TYPES.ON_DEMAND);

    const timeCardsClass = 'time-cards__request-credit';
    const sectionHeadingClass = 'request-credit__section-heading';
    const isSingleSegment = (useAppSelector((state) => state.player.launchDetails.segments?.length ?? 0) <= 1);

    if (isGetTimeCardsError) {
        showBoundary(new Error('Error fetching Time Cards'));
        return null;
    }

    const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    if (isLoadingTimeRecords) {
        return (
            <div className="h-75">
                <Loader />
            </div>
        );
    }

    if (!isGetTimeCardsSuccess || !hasEither) {
        return null;
    }

    return (
        <>
            <div className={hasBoth ? '' : 'time-cards'} onFocus={hasBoth ? undefined : handleFocus}>
                {hasOnDemand && (
                    <>
                        <SectionHeader
                            title={'On-Demand'}
                            showToggle={showOdToggle}
                            isExpanded={isExpanded(RECORD_TYPES.ON_DEMAND)}
                            onToggle={() => toggleSection(RECORD_TYPES.ON_DEMAND)}
                            sectionClass={sectionHeadingClass}
                            hasBorderBottom={!isExpanded(RECORD_TYPES.ON_DEMAND) && !onDemandCleRecords?.some((r) => (r.percentComplete ?? 0) > 0)}
                        />

                        <ul className={timeCardsClass}>
                            {visibleOnDemandRecords.map((record) => (
                                <TimeCard
                                    key={record.prgSegment_SK ?? ''}
                                    timeRecord={record}
                                    onClose={onClose}
                                    isSingleSegment={isSingleSegment}
                                />
                            ))}
                        </ul>
                    </>
                )}

                {hasLive && (
                    <>
                        <SectionHeader
                            title={'Live'}
                            showToggle={showLiveToggle}
                            isExpanded={isExpanded(RECORD_TYPES.LIVE)}
                            onToggle={() => toggleSection(RECORD_TYPES.LIVE)}
                            sectionClass={sectionHeadingClass}
                            hasBorderBottom={!isExpanded(RECORD_TYPES.LIVE) && !liveCleRecords?.some((r) => (r.percentComplete ?? 0) > 0)}
                        />

                        <ul className={timeCardsClass}>
                            {visibleLiveRecords.map((record) => (
                                <TimeCard
                                    key={record.prgSegment_SK ?? ''}
                                    timeRecord={record}
                                    onClose={onClose}
                                    isSingleSegment={isSingleSegment}
                                />
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </>
    );
};

const SectionHeader: React.FC<{
    title: string;
    showToggle: boolean;
    isExpanded: boolean;
    onToggle: () => void;
    sectionClass: string;
    hasBorderBottom: boolean;
}> = ({ title, showToggle, isExpanded, onToggle, sectionClass, hasBorderBottom }) => (
    <div
        className={`d-flex justify-content-between align-items-center ${hasBorderBottom ? 'request-credit__section-heading-border-bottom' : ''} ${sectionClass}`}
    >
        <div className="time-card__type-heading paragraph-2 mb-0">{title}</div>

        {showToggle && (
            <Button
                buttonClass="time-card__edit time-card__collapse-button"
                label={`${isExpanded ? 'Collapse' : 'Show'} all segments`}
                action={onToggle}
                aria-label={`Toggle ${title} sections`}
                icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                iconPosition="right"
            />
        )}
    </div>
);

export const RequestCreditTimeCards = withPLIErrorBoundary(RequestCreditTimeCardsBase, null, {
    errorContainer: ErrorContainer.LargePanel,
});